import { ThemeProvider } from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";

import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";

import "./assets/styles/fonts.css";
import { AlertProvider } from "./components/common/Alert/AlertContext";
import Loader from "./components/route/Loader";
import PrivateRoute from "./components/route/PrivateRoute";
import theme from "./theme";
import { generateToken, initializeMessaging } from "./notifications/firebase";
import { onMessage } from "firebase/messaging";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { AuthorizationActions } from "./components/api/RequestUtil";

import { ErrorBoundary } from "react-error-boundary";
import Error from "./components/Error";
import {
  AdminApproveExerciseRoute,
  AdminCreateFormRoute,
  AdminEditFormRoute,
  AdminFormsRoute,
  AdminLandingPageRoute,
  AdminServicesRoute,
  AdminSettingsRoute,
  AdminUsersRoute,
  AssessmentRoute,
  BlueprintsRoute,
  CalendarRoute,
  ClientProfileRoute,
  ClientsRoute,
  DashBoardRoute,
  DashBoardPaymentsRoute,
  CompletedPaymentsRoute,
  UpcomingPaymentsRoute,
  InvoicesRoute,
  DynamicBuilderEditRoute,
  DynamicBuilderRoute,
  ForgotPasswordRoute,
  LandingPageRoute,
  MarketHubRoute,
  OnBoardingRoute,
  ResetPasswordRoute,
  SignInRoute,
  SignUpRoute,
  TasksRoute,
  VerifyOTPRoute,
  SetPasswordRoute,
  ClientViewProgram,
  MessageRoute,
  CheckInFormDetailsRoute,
  ClientNewBlankSessionRoute,
  RecordSessionProgramWorkoutRoute,
  NewLeadClientRoute,
  ViewSessionDetailRoute,
  ViewSessionFormDetailRoute,
} from "./components/route/routes";
import NewLeadClientDetails from "./components/clients/new-lead-onboarding";
import CardDetails from "./pages/CardDetails";
import DeleteAccount from "./pages/DeleteAccount";

// Retry a lazy import up to `retries` times with exponential back-off.
// This handles flaky internet connections where a chunk fetch fails transiently.
const lazyWithRetry = (importFn, retries = 3, interval = 1000) =>
  lazy(
    () =>
      new Promise((resolve, reject) => {
        const attempt = (triesLeft) => {
          importFn()
            .then(resolve)
            .catch((error) => {
              if (triesLeft === 0) {
                reject(error);
                return;
              }
              setTimeout(() => attempt(triesLeft - 1), interval * (retries - triesLeft + 1));
            });
        };
        attempt(retries);
      })
  );

const Assessments = lazyWithRetry(() => import("./components/assessments"));
const BluePrints = lazyWithRetry(() => import("./components/bluePrints"));
const ViewBlueprintProgram = lazyWithRetry(() => import("./components/bluePrints/ViewBlueprintProgram"));
const Dashboard = lazyWithRetry(() => import("./components/dashboard"));
const DashboardPayments = lazyWithRetry(() => import("./components/dashboard/payments"));
const UpcomingPayments = lazyWithRetry(() => import("./components/payments/UpcomingPayments"));
const Invoices = lazyWithRetry(() => import("./components/payments/Invoices"));
const DynamicBuilder = lazyWithRetry(() => import("./components/dynamicBuilder"));
const EditDynamicBuilder = lazyWithRetry(() => import("./components/dynamicBuilder/EditDynamicBuilder"));
const ForgotPassword = lazyWithRetry(() => import("./components/onBoarding/ForgotPassword"));
const NotFound = lazyWithRetry(() => import("./components/route/NotFound"));
const OnBoarding = lazyWithRetry(() => import("./components/onBoarding"));
const ResetPassword = lazyWithRetry(() => import("./components/onBoarding/ResetPassword"));
const SignIn = lazyWithRetry(() => import("./components/signIn"));
const SignUp = lazyWithRetry(() => import("./components/onBoarding/SignUp"));
const Users = lazyWithRetry(() => import("./components/users"));
const Clients = lazyWithRetry(() => import("./components/clients"));
const ClientProfile = lazyWithRetry(() => import("./components/clients/clientProfile"));
const Forms = lazyWithRetry(() => import("./components/forms"));
const CreateForm = lazyWithRetry(() => import("./components/forms/createForm"));
const EditForm = lazyWithRetry(() => import("./components/forms/EditForm"));
const ServicesAndSubscriptions = lazyWithRetry(() => import("./components/services-and-subscriptions"));
const Settings = lazyWithRetry(() => import("./components/settings"));
const Tasks = lazyWithRetry(() => import("./components/tasks"));
const MarketHub = lazyWithRetry(() => import("./components/marketHub"));
const AdminLandingPage = lazyWithRetry(() => import("./components/landing-page/admin-landing-page"));
const LandingPage = lazyWithRetry(() => import("./components/landing-page"));
const Calendar = lazyWithRetry(() => import("./components/calendar"));
const ApproveExercise = lazyWithRetry(() => import("./components/approve-exercise"));
const VerifyOTP = lazyWithRetry(() => import("./components/onBoarding/VerifyOTP"));
const SetPassword = lazyWithRetry(() => import("./components/onBoarding/SetPassword"));
const ViewProgram = lazyWithRetry(() => import("./components/clients/clientProfile/view-program"));
const Messages = lazyWithRetry(() => import("./components/messages/MessagesPage"));
const ClientCheckInFormDetails = lazyWithRetry(() => import("./components/forms/ClientCheckInFormDetails"));
const CreateNewBlankSession = lazyWithRetry(() => import("./components/clients/clientProfile/blank-session"));
const RecordSessionProgramWorkout = lazyWithRetry(
  () => import("./components/clients/clientProfile/record-session-program-workout")
);
const ViewSessionDetails = lazyWithRetry(
  () => import("./components/clients/clientProfile/session-notes/ViewSessionDetails")
);
const SessionFeedbackFormDetails = lazyWithRetry(() => import("./components/forms/SessionFeedbackFormDetails"));

const ErrorBoundaryLayout = () => (
  <ErrorBoundary FallbackComponent={Error}>
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
  </ErrorBoundary>
);

const App = () => {
  const router = createBrowserRouter([
    {
      element: <ErrorBoundaryLayout />,
      children: [
        // Public route for client card details
        {
          path: "/client/payment-entry",
          element: <CardDetails open={true} handleClose={() => {}} clientId={null} />,
        },
        // Public route for client account deletion (Google Play Data Safety)
        {
          path: "/delete-account",
          element: <DeleteAccount />,
        },
        {
          path: "/",
          element: <PrivateRoute />,
          children: [
            {
              path: "/",
              element: <Navigate to={SignInRoute} replace />,
            },
            {
              path: SignInRoute,
              element: <SignIn />,
            },
            {
              path: SignUpRoute,
              element: <SignUp />,
            },
            {
              path: ForgotPasswordRoute,
              element: <ForgotPassword />,
            },
            {
              path: ResetPasswordRoute,
              element: <ResetPassword />,
            },
            {
              path: VerifyOTPRoute,
              element: <VerifyOTP />,
            },
            {
              path: SetPasswordRoute,
              element: <SetPassword />,
            },
            {
              path: DashBoardRoute,
              element: <Dashboard />,
            },
            {
              path: DashBoardPaymentsRoute,
              element: <DashboardPayments />,
            },
            {
              path: CompletedPaymentsRoute,
              element: <DashboardPayments />,
            },
            {
              path: UpcomingPaymentsRoute,
              element: <UpcomingPayments />,
            },
            {
              path: InvoicesRoute,
              element: <Invoices />,
            },
            {
              path: OnBoardingRoute,
              element: <OnBoarding />,
            },
            {
              path: ClientsRoute,
              element: <Clients />,
            },
            {
              path: ClientProfileRoute,
              element: <ClientProfile />,
            },
            {
              path: ClientViewProgram,
              element: <ViewProgram />,
            },
            {
              path: ClientNewBlankSessionRoute,
              element: <CreateNewBlankSession />,
            },
            {
              path: RecordSessionProgramWorkoutRoute,
              element: <RecordSessionProgramWorkout />,
            },
            {
              path: DynamicBuilderRoute,
              element: <DynamicBuilder />,
            },
            {
              path: DynamicBuilderEditRoute,
              element: <EditDynamicBuilder />,
            },
            {
              path: BlueprintsRoute,
              element: <BluePrints />,
            },
            {
              path: "/blueprints/view/:blueprintId",
              element: <ViewBlueprintProgram />,
            },
            {
              path: AdminUsersRoute,
              element: <Users />,
            },
            {
              path: AdminFormsRoute,
              element: <Forms />,
            },
            {
              path: AdminCreateFormRoute,
              element: <CreateForm />,
            },
            {
              path: AdminEditFormRoute,
              element: <EditForm />,
            },
            {
              path: AdminServicesRoute,
              element: <ServicesAndSubscriptions />,
            },
            {
              path: AssessmentRoute,
              element: <Assessments />,
            },
            {
              path: AdminSettingsRoute,
              element: <Settings />,
            },
            {
              path: TasksRoute,
              element: <Tasks />,
            },
            {
              path: MarketHubRoute,
              element: <MarketHub />,
            },
            {
              path: AdminLandingPageRoute,
              element: <AdminLandingPage />,
            },
            {
              path: CalendarRoute,
              element: <Calendar />,
            },
            {
              path: MessageRoute,
              element: <Messages />,
            },
            {
              path: CheckInFormDetailsRoute,
              element: <ClientCheckInFormDetails />,
            },
            {
              path: NewLeadClientRoute,
              element: <NewLeadClientDetails />,
            },
            {
              path: ViewSessionDetailRoute,
              element: <ViewSessionDetails />,
            },
            {
              path: ViewSessionFormDetailRoute,
              element: <SessionFeedbackFormDetails />,
            },
          ],
        },
        {
          path: LandingPageRoute,
          element: <LandingPage />,
        },
        {
          path: AdminApproveExerciseRoute,
          element: <ApproveExercise />,
        },
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
  ]);

  const [alertMessage, setAlertMessage] = useState({ body: "", title: "" });
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    const setupNotifications = async () => {
      // Initialize Firebase Messaging first
      const messagingInstance = await initializeMessaging();

      // Register token
      const tokenData = await generateToken();

      // Only send token if user is logged in
      const user = localStorage.getItem("user");
      if (tokenData && user) {
        try {
          await AuthorizationActions.sendTokenToServer(tokenData);
        } catch (error) {
          console.error("Error sending FCM token to server:", error);
        }
      }

      // Setup foreground message listener
      if (messagingInstance) {
        onMessage(messagingInstance, (payload) => {
          // Show in-app alert
          setAlertMessage({
            body: payload.notification.body,
            title: payload.notification.title,
          });
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 6000);

          // Also show OS browser notification
          if (Notification.permission === "granted") {
            new Notification("Nativus Coach", {
              body: `${payload.notification.title.toUpperCase()}\n${payload.notification.body}`,
              icon: "/logo192.png",
              badge: "/logo192.png",
              tag: payload.notification.title,
            });
          }
        });
      }
    };

    setupNotifications();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <AlertProvider>
        {showAlert && (
          <Alert
            severity="info"
            onClose={() => setShowAlert(false)}
            sx={{ position: "fixed", top: 10, right: 10, zIndex: 1000 }}
          >
            <AlertTitle>{alertMessage.title}</AlertTitle>
            {alertMessage.body}
          </Alert>
        )}
        <RouterProvider router={router} />
      </AlertProvider>
    </ThemeProvider>
  );
};

export default App;
